// src/components/CurrencyCard.js
//
// Dashboard card showing medical, flight review (61.56) and recency from
// /api/currency. Medical / flight review dates and the pilot's operating
// rules (Part 91 or Part 121) are edited inline. In Part 121 mode, landing
// currency per type (121.439) leads and 61.57 moves under personal flying.

import { useEffect, useState } from 'react';
import { ShieldCheck, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import axios from 'axios';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import toast from 'react-hot-toast';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
});

const STATUS_STYLES = {
  current: { label: 'Current', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  expiring: { label: 'Expiring soon', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  grace: { label: 'Lapsed — grace period', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  expired: { label: 'Not current', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  unset: { label: 'Not set', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
};

const CLASS_NAMES = { 1: 'First class', 2: 'Second class', 3: 'Third class' };
const ORDINAL = { 1: '1st', 2: '2nd', 3: '3rd' };

const formatDate = (dateStr) => format(parseISO(dateStr), 'MMM d, yyyy');

const StatusChip = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.expired;
  return (
    <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${style.className}`}>
      {style.label}
    </span>
  );
};

// "Current through Dec 29, 2026 (77 days)" / "Lapsed Jul 28, 2026"
const expiryText = (item, asOf) => {
  if (!item.expiresOn) return 'No qualifying activity in the last 13 months';
  if (item.status === 'current' || item.status === 'expiring') {
    const days = differenceInCalendarDays(parseISO(item.expiresOn), parseISO(asOf));
    return `Current through ${formatDate(item.expiresOn)} (${days} day${days === 1 ? '' : 's'})`;
  }
  return `Lapsed ${formatDate(item.expiresOn)}`;
};

// Medical / flight review: "Valid through …" rather than recency wording
const certificateExpiryText = (item, asOf) => {
  if (item.status === 'expired') return `Expired ${formatDate(item.expiresOn)}`;
  const days = differenceInCalendarDays(parseISO(item.expiresOn), parseISO(asOf));
  return `Valid through ${formatDate(item.expiresOn)} (${days} day${days === 1 ? '' : 's'})`;
};

const inputClasses = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500';

const CertificateEditor = ({ profile, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    medical_class: profile.medical_class ? String(profile.medical_class) : '',
    medical_exam_date: profile.medical_exam_date || '',
    medical_under_40: profile.medical_under_40,
    medical_required_class: profile.medical_required_class ? String(profile.medical_required_class) : '',
    flight_review_date: profile.flight_review_date || '',
    operating_rules: profile.operating_rules || 'part91'
  });
  const [saving, setSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const set = (key) => (e) => setForm(f => {
    const next = { ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value };
    // Drop a required class the newly chosen medical class can't provide
    if (key === 'medical_class' && next.medical_class && Number(next.medical_required_class) < Number(next.medical_class)) {
      next.medical_required_class = '';
    }
    return next;
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/currency/profile', form, getAuthHeaders());
      onSaved(res.data);
      toast.success('Currency settings saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Required class can't be higher than the class issued
  const requiredOptions = [1, 2, 3].filter(c => !form.medical_class || c >= Number(form.medical_class));

  return (
    <form onSubmit={save} className="py-3 border-b border-gray-100 space-y-3">
      <label className="block text-xs font-medium text-gray-700">
        I fly under
        <select value={form.operating_rules} onChange={set('operating_rules')} className={`${inputClasses} mt-1`}>
          <option value="part91">Part 91 — general aviation</option>
          <option value="part121">Part 121 — airline</option>
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-medium text-gray-700">
          Medical class
          <select value={form.medical_class} onChange={set('medical_class')} className={`${inputClasses} mt-1`}>
            <option value="">None</option>
            {[1, 2, 3].map(c => <option key={c} value={c}>{CLASS_NAMES[c]}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700">
          Exam date
          <input type="date" max={today} value={form.medical_exam_date} onChange={set('medical_exam_date')} className={`${inputClasses} mt-1`} />
        </label>
        <label className="text-xs font-medium text-gray-700">
          Privileges you need
          <select value={form.medical_required_class} onChange={set('medical_required_class')} className={`${inputClasses} mt-1`}>
            <option value="">Same as issued</option>
            {requiredOptions.map(c => <option key={c} value={c}>{ORDINAL[c]}-class</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700 flex items-end pb-2">
          <input type="checkbox" checked={form.medical_under_40} onChange={set('medical_under_40')} className="h-4 w-4 mr-2 text-sky-600 rounded" />
          Under 40 on exam date
        </label>
      </div>
      <label className="block text-xs font-medium text-gray-700">
        {form.operating_rules === 'part121' ? 'Last proficiency check (PC) or flight review' : 'Last flight review or proficiency check'}
        <input type="date" max={today} value={form.flight_review_date} onChange={set('flight_review_date')} className={`${inputClasses} mt-1`} />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm rounded-lg text-white bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

const Count = ({ label, value, required }) => (
  <span className={value >= required ? 'text-gray-700' : 'text-red-600 font-semibold'}>
    {label} {value}/{required}
  </span>
);

const CurrencyRow = ({ title, regulation, status, children }) => (
  <div className="py-3 border-b border-gray-100 last:border-b-0">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500">{regulation}</p>
      </div>
      <StatusChip status={status} />
    </div>
    <div className="mt-1.5 text-sm text-gray-600 space-y-0.5">{children}</div>
  </div>
);

const CurrencyCard = () => {
  const [currency, setCurrency] = useState(null);
  const [error, setError] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [editingCertificates, setEditingCertificates] = useState(false);
  const [showPersonal, setShowPersonal] = useState(false);

  useEffect(() => {
    axios.get('/api/currency', getAuthHeaders())
      .then(res => setCurrency(res.data))
      .catch(err => {
        console.error('Error loading currency:', err);
        setError(true);
      });
  }, []);

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center mb-2">
          <ShieldCheck className="h-6 w-6 mr-2 text-sky-600" /> Currency
        </h2>
        <p className="text-sm text-gray-500">Couldn't load currency right now.</p>
      </div>
    );
  }

  if (!currency) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
        <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
        <div className="h-4 w-full bg-gray-100 rounded mb-2"></div>
        <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
      </div>
    );
  }

  const { asOf, passenger, night, instrument, byType, medical, flightReview, profile } = currency;
  const is121 = profile?.operating_rules === 'part121';
  // Server sorts by most recently flown; show the top few by default
  const typesToShow = showAllTypes ? byType : byType.slice(0, 3);

  const openEditor = () => setEditingCertificates(true);

  const certificateRows = editingCertificates ? (
    <CertificateEditor
      profile={profile || {}}
      onCancel={() => setEditingCertificates(false)}
      onSaved={(saved) => {
        setCurrency(c => ({ ...c, ...saved }));
        setEditingCertificates(false);
      }}
    />
  ) : (
    <>
      <CurrencyRow
        title={medical ? `Medical — ${CLASS_NAMES[medical.medicalClass]}` : 'Medical'}
        regulation={medical ? `14 CFR 61.23 · ${ORDINAL[medical.requiredClass]}-class privileges` : '14 CFR 61.23'}
        status={medical ? medical.status : 'unset'}
      >
        {medical ? (
          <>
            <p>{certificateExpiryText(medical, asOf)}</p>
            {medical.privileges.length > 1 && (
              <p className="text-xs text-gray-500">
                {medical.privileges.map(p => `${ORDINAL[p.class]} through ${format(parseISO(p.expiresOn), 'MMM yyyy')}`).join(' · ')}
              </p>
            )}
          </>
        ) : (
          <button type="button" onClick={openEditor} className="text-sky-600 hover:text-sky-800 font-medium">
            Add your medical
          </button>
        )}
      </CurrencyRow>

      <CurrencyRow
        title={is121 ? 'Proficiency check / flight review' : 'Flight review'}
        regulation={is121
          ? '14 CFR 61.56(d) · a Part 121 PC within 24 months satisfies it'
          : '14 CFR 61.56 · every 24 calendar months'}
        status={flightReview ? flightReview.status : 'unset'}
      >
        {flightReview ? (
          <p>{certificateExpiryText(flightReview, asOf)}</p>
        ) : (
          <button type="button" onClick={openEditor} className="text-sky-600 hover:text-sky-800 font-medium">
            {is121 ? 'Add your last proficiency check' : 'Add your last flight review or proficiency check'}
          </button>
        )}
      </CurrencyRow>

      <div className="flex justify-end -mt-1 mb-1">
        <button type="button" onClick={openEditor} className="inline-flex items-center text-xs text-sky-600 hover:text-sky-800 font-medium">
          <Pencil className="h-3 w-3 mr-1" /> Edit medical, reviews &amp; Part 91/121
        </button>
      </div>
    </>
  );

  const recencyRows = (
    <>
      <CurrencyRow title="Passenger carrying" regulation="14 CFR 61.57(a) · 3 takeoffs & landings / 90 days" status={passenger.status}>
        <p>
          <Count label="Takeoffs" value={passenger.takeoffs} required={passenger.required} />
          {' · '}
          <Count label="Landings" value={passenger.landings} required={passenger.required} />
        </p>
        <p>{expiryText(passenger, asOf)}</p>
      </CurrencyRow>

      <CurrencyRow title="Night" regulation="14 CFR 61.57(b) · 3 night takeoffs & full-stop landings / 90 days" status={night.status}>
        <p>
          <Count label="Takeoffs" value={night.takeoffs} required={night.required} />
          {' · '}
          <Count label="Landings" value={night.landings} required={night.required} />
        </p>
        <p>{expiryText(night, asOf)}</p>
      </CurrencyRow>

      <CurrencyRow title="Instrument" regulation="14 CFR 61.57(c) · 6 approaches / 6 calendar months" status={instrument.status}>
        <p><Count label="Approaches" value={instrument.approaches} required={instrument.required} /></p>
        <p>{expiryText(instrument, asOf)}</p>
        {instrument.status === 'grace' && (
          <p className="text-amber-700">
            Regain with a safety pilot or in a sim by {formatDate(instrument.graceEndsOn)} — after that an IPC is required.
          </p>
        )}
        {instrument.status === 'expired' && (
          <p className="text-red-600">Grace period over — an instrument proficiency check (IPC) is required.</p>
        )}
      </CurrencyRow>
    </>
  );

  const typeSection = byType.length > 0 && (
    <div className={is121 ? 'py-3 border-b border-gray-100' : 'mt-4'}>
      <h3 className={is121 ? 'font-semibold text-gray-900' : 'text-sm font-semibold text-gray-700 mb-2'}>
        {is121 ? 'Landing currency by type' : 'By type rating (last 12 months)'}
      </h3>
      {is121 && (
        <p className="text-xs text-gray-500 mb-2">14 CFR 121.439 · 3 takeoffs &amp; landings in type / 90 days</p>
      )}
      <ul className="space-y-2">
        {typesToShow.map(t => (
          <li key={t.rating} className="flex items-start justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{t.rating}</p>
              {t.types.length > 1 || t.types[0] !== t.rating ? (
                <p className="text-xs text-gray-500 truncate">{t.types.join(', ')}</p>
              ) : null}
              <p className="text-xs">
                <Count label="Takeoffs" value={t.day.takeoffs} required={t.day.required} />
                <span className="text-gray-400">{' · '}</span>
                <Count label="Landings" value={t.day.landings} required={t.day.required} />
              </p>
              <p className="text-xs text-gray-500">{expiryText(t.day, asOf)}</p>
            </div>
            <StatusChip status={t.day.status} />
          </li>
        ))}
      </ul>
      {byType.length > 3 && (
        <button
          type="button"
          onClick={() => setShowAllTypes(v => !v)}
          className="mt-2 inline-flex items-center text-sm text-sky-600 hover:text-sky-800 font-medium"
        >
          {showAllTypes ? <>Show fewer <ChevronUp className="h-4 w-4 ml-1" /></> : <>Show all {byType.length} <ChevronDown className="h-4 w-4 ml-1" /></>}
        </button>
      )}
    </div>
  );

  // Most urgent 61.57 status, shown while the personal-flying section is collapsed
  const personalStatus = ['expired', 'grace', 'expiring', 'current']
    .find(st => [passenger, night, instrument].some(item => item.status === st));

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <ShieldCheck className="h-6 w-6 mr-2 text-sky-600" />
          Currency
          {is121 && <span className="ml-2 text-xs font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full">Part 121</span>}
        </h2>
        <span className="text-xs text-gray-500">as of {formatDate(asOf)}</span>
      </div>

      {certificateRows}

      {is121 ? (
        <>
          {typeSection}
          <div className="py-3">
            <button
              type="button"
              onClick={() => setShowPersonal(v => !v)}
              aria-expanded={showPersonal}
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <span>
                <span className="block font-semibold text-gray-900">Personal flying (Part 91)</span>
                <span className="block text-xs text-gray-500">14 CFR 61.57 · passenger, night &amp; instrument recency</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {!showPersonal && <StatusChip status={personalStatus} />}
                {showPersonal ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
              </span>
            </button>
            {showPersonal && <div className="mt-1">{recencyRows}</div>}
          </div>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed">
            Under 61.57(e), 61.57 recency doesn't apply while you fly for a Part 121/135
            carrier in compliance with 121.437 and 121.439 — it still applies to personal
            flying. Calculated from logged takeoffs, landings and approaches; confirm
            details against your operator's requirements.
          </p>
        </>
      ) : (
        <>
          {recencyRows}
          {typeSection}
          <p className="mt-4 text-xs text-gray-500 leading-relaxed">
            Calculated from logged takeoffs, landings and approaches. Holding and
            intercepting/tracking aren't logged — confirm those yourself. Airline pilot?
            Switch to Part 121 under "Edit" above.
          </p>
        </>
      )}
    </div>
  );
};

export default CurrencyCard;
